import React, { useState } from 'react';
import {
  ChatBubbleLeftRightIcon, StarIcon, HandThumbUpIcon, HandThumbDownIcon,
  ExclamationTriangleIcon, CheckCircleIcon, ClockIcon, ArrowDownTrayIcon,
  ChartBarIcon, UserGroupIcon, HeartIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Badge, { BadgeTone } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';

const FEEDBACK_STATUS_TONE: Record<string, BadgeTone> = {
  resolved: 'success',
  in_progress: 'info',
  pending: 'warning',
  closed: 'neutral',
};
const FEEDBACK_PRIORITY_TONE: Record<string, BadgeTone> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

interface Feedback {
  id: string;
  citizenId: string;
  citizenName: string;
  reportId: string;
  type: 'general' | 'service' | 'complaint' | 'suggestion';
  category: string;
  rating: number;
  title: string;
  description: string;
  tags: string[];
  location: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  sentiment: 'positive' | 'neutral' | 'negative';
  satisfaction: number;
  createdAt: string;
  updatedAt: string;
}

interface FeedbackSummary {
  totalFeedback: number;
  averageRating: number;
  npsScore: number;
  byStatus: { status: string; count: number }[];
  byPriority: { priority: string; count: number }[];
  bySentiment: { sentiment: string; count: number }[];
  recentFeedback: Feedback[];
}

const FeedbackSystem: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedSentiment, setSelectedSentiment] = useState<string>('all');
  const queryClient = useQueryClient();

  // Fetch feedback data
  const { data: feedback, isLoading, error } = useQuery({
    queryKey: ['feedback', selectedStatus, selectedPriority, selectedSentiment],
    queryFn: async () => {
      const params = new URLSearchParams({
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedPriority !== 'all' && { priority: selectedPriority }),
        ...(selectedSentiment !== 'all' && { sentiment: selectedSentiment })
      });
      const response = await apiService.get(`/feedback?${params}`);
      // apiService.get already returns response.data, and backend returns { success: true, data: [...] }
      return response.success ? response.data : [];
    }
  });

  // Fetch feedback summary
  const { data: summary } = useQuery({
    queryKey: ['feedback-summary'],
    queryFn: async () => {
      const response = await apiService.get('/feedback/summary');
      // apiService.get already returns response.data, and backend returns { success: true, data: {...} }
      return response.success ? response.data : null;
    }
  });

  // Update feedback status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await apiService.patch(`/feedback/${id}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-summary'] });
    }
  });

  // Export feedback
  const exportMutation = useMutation({
    mutationFn: async (format: 'csv' | 'json') => {
      const params = new URLSearchParams({
        format,
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedPriority !== 'all' && { priority: selectedPriority }),
        ...(selectedSentiment !== 'all' && { sentiment: selectedSentiment })
      });
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/feedback/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      return response.blob();
    },
    onSuccess: (blob, format) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  });

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <HandThumbUpIcon className="h-4 w-4 text-green-600" />;
      case 'negative': return <HandThumbDownIcon className="h-4 w-4 text-red-600" />;
      case 'neutral': return <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      default: return <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading feedback</h3>
              <div className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error occurred'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Feedback System" subtitle="Manage citizen feedback, track satisfaction, and monitor engagement" />

      {/* Filters and Export */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-4">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          <select
            value={selectedSentiment}
            onChange={(e) => setSelectedSentiment(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Sentiments</option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
          </select>
        </div>

        <div className="flex gap-2">
          <Button variant="secondary" icon={ArrowDownTrayIcon} disabled={exportMutation.isPending} onClick={() => exportMutation.mutate('csv')}>
            Export CSV
          </Button>
          <Button variant="secondary" icon={ArrowDownTrayIcon} disabled={exportMutation.isPending} onClick={() => exportMutation.mutate('json')}>
            Export JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Feedback" value={summary.totalFeedback} icon={ChatBubbleLeftRightIcon} tone="blue" />
          <StatCard label="Average Rating" value={`${summary.averageRating.toFixed(1)}/5`} icon={StarIcon} tone="amber" />
          <StatCard label="NPS Score" value={summary.npsScore} icon={ChartBarIcon} tone="emerald" />
          <StatCard
            label="Satisfaction"
            value={summary.bySentiment.find((s: { sentiment: string; count: number }) => s.sentiment === 'positive')?.count || 0}
            icon={HeartIcon}
            tone="purple"
          />
        </div>
      )}

      {/* Feedback Table */}
      <div className="rounded-sm border border-gray-300 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Citizen Feedback</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Citizen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Sentiment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
              {feedback && Array.isArray(feedback) && feedback.length > 0 ? (
                feedback.map((item: Feedback) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.citizenName || 'Unknown'}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{item.citizenId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {renderStars(item.rating)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">({item.rating})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge tone={FEEDBACK_STATUS_TONE[item.status] ?? 'neutral'}>{item.status.replace('_', ' ')}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge tone={FEEDBACK_PRIORITY_TONE[item.priority] ?? 'neutral'}>{item.priority}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getSentimentIcon(item.sentiment)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-gray-100 capitalize">{item.sentiment}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <select
                        value={item.status}
                        onChange={(e) => updateStatusMutation.mutate({ id: item.id, status: e.target.value })}
                        className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-8">
                    <EmptyState
                      icon={ChatBubbleLeftRightIcon}
                      title="No feedback yet"
                      description="Citizen feedback will appear here once submitted."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeedbackSystem;
